<template>
  <div class="reserve-details">
    <template v-if="hasData">
      <header class="rd-header box">
        <div class="rd-header__main">
          <p class="rd-header__eyebrow">
            <b-icon
              pack="fas"
              icon="calendar-week"
              size="is-small"
            ></b-icon>
            {{ summary.title }}
          </p>
          <h1 v-if="raw.ResortName" class="rd-header__resort">
            {{ raw.ResortName }}
          </h1>
          <p class="rd-header__subtitle">{{ summary.subtitle }}</p>
          <div class="rd-header__meta">
            <b-tag v-if="numberRef" type="is-light" rounded>
              {{ summary.reference }}: {{ numberRef }}
            </b-tag>
            <b-tag
              v-if="raw.InvTypeInDetail"
              type="is-primary is-light"
              rounded
            >
              {{ raw.InvTypeInDetail }}
            </b-tag>
          </div>
        </div>
        <b-button
          class="rd-header__close"
          icon-left="times"
          icon-pack="fas"
          type="is-light"
          @click="closePage"
        ></b-button>
      </header>

      <div class="columns is-multiline rd-top">
        <div class="column is-7">
          <div class="box rd-carousel">
            <b-carousel
              v-if="images.length"
              :autoplay="false"
              :indicator="images.length > 1"
              :arrow="images.length > 1 ? 'hover' : 'never'"
            >
              <b-carousel-item v-for="(img, i) in images" :key="i">
                <figure class="rd-carousel__item">
                  <img
                    :src="img"
                    :alt="`${raw.ResortName || 'Resort'} ${i + 1}`"
                    loading="lazy"
                  />
                </figure>
              </b-carousel-item>
            </b-carousel>
            <div v-else class="rd-carousel__empty">
              <div class="rd-carousel__empty-body">
                <b-icon pack="fas" icon="image" size="is-large"></b-icon>
                <p>{{ summary.noImages }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="column is-5">
          <div v-if="stats.length" class="rd-stats">
            <div v-for="stat in stats" :key="stat.key" class="rd-stat">
              <span class="rd-stat__icon">
                <b-icon
                  pack="fas"
                  :icon="stat.icon"
                  size="is-small"
                ></b-icon>
              </span>
              <div class="rd-stat__body">
                <span class="rd-stat__label">{{ stat.label }}</span>
                <span class="rd-stat__value">{{ stat.value }}</span>
              </div>
            </div>
          </div>

          <div class="box rd-card">
            <h3 class="rd-card__title">
              <b-icon pack="fas" icon="hotel" size="is-small"></b-icon>
              {{ summary.unit }}
            </h3>
            <ul class="rd-list">
              <li v-for="item in unitFields" :key="item.key">
                <span class="rd-list__label">{{ item.label }}</span>
                <b-tag
                  v-if="item.key === 'AllInclusive'"
                  :type="
                    allInclusive
                      ? 'is-success is-light'
                      : 'is-danger is-light'
                  "
                  rounded
                >
                  {{ allInclusive ? "Sí" : "No" }}
                </b-tag>
                <span v-else class="rd-list__value">{{ item.value }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="box rd-card">
        <div class="rd-card__head">
          <h3 class="rd-card__title">
            <b-icon
              pack="fas"
              icon="map-marker-alt"
              size="is-small"
            ></b-icon>
            {{ summary.location }}
          </h3>
          <b-button
            v-if="hasCoordinates"
            type="is-info is-light"
            size="is-small"
            icon-left="external-link-alt"
            icon-pack="fas"
            @click="openGoogleMap"
          >
            {{ summary.openMap }}
          </b-button>
        </div>

        <div class="rd-location">
          <p v-if="fullAddress" class="rd-location__address">
            {{ fullAddress }}
          </p>
          <div class="rd-location__grid">
            <div v-if="raw.ResortRegion" class="rd-location__item">
              <span class="rd-list__label">{{ labels.ResortRegion }}</span>
              <span class="rd-list__value">{{ raw.ResortRegion }}</span>
            </div>
            <div v-if="raw.ResortPhone" class="rd-location__item">
              <span class="rd-list__label">{{ labels.ResortPhone }}</span>
              <a
                class="rd-list__value rd-list__link"
                :href="`tel:${raw.ResortPhone}`"
              >
                <b-icon pack="fas" icon="phone" size="is-small"></b-icon>
                {{ raw.ResortPhone }}
              </a>
            </div>
            <div v-if="raw.Rating !== undefined" class="rd-location__item">
              <span class="rd-list__label">{{ labels.Rating }}</span>
              <span class="rd-list__value">
                <b-icon
                  pack="fas"
                  icon="star"
                  size="is-small"
                  type="is-warning"
                ></b-icon>
                {{ raw.Rating }}
              </span>
            </div>
            <div v-if="hasCoordinates" class="rd-location__item">
              <span class="rd-list__label">{{ labels.GPS_Lat }} /
                {{ labels.GPS_Long }}</span>
              <span class="rd-list__value">
                {{ raw.GPS_Lat }}, {{ raw.GPS_Long }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="highlights" class="box rd-card">
        <h3 class="rd-card__title">
          <b-icon pack="fas" icon="align-left" size="is-small"></b-icon>
          {{ i18n.description[iso] }}
        </h3>
        <p class="rd-text">{{ highlights }}</p>
      </div>

      <div v-if="importantNotes" class="box rd-card">
        <h3 class="rd-card__title">
          <b-icon
            pack="fas"
            icon="exclamation-circle"
            size="is-small"
          ></b-icon>
          {{ i18n.notesImportant[iso] }}
        </h3>
        <p class="rd-text rd-text--pre">{{ importantNotes }}</p>
      </div>

      <div v-if="amenityGroups.length" class="box rd-card">
        <h3 class="rd-card__title">
          <b-icon
            pack="fas"
            icon="concierge-bell"
            size="is-small"
          ></b-icon>
          {{ summary.amenities }}
        </h3>
        <div
          v-for="group in amenityGroups"
          :key="group.name"
          class="rd-amenity-group"
        >
          <p class="rd-amenity-group__title">{{ group.name }}</p>
          <div class="rd-amenity-group__items">
            <div
              v-for="(amenity, i) in group.items"
              :key="i"
              class="rd-amenity"
            >
              <b-icon
                pack="fas"
                icon="check-circle"
                size="is-small"
                type="is-success"
              ></b-icon>
              <span class="rd-amenity__name">{{ amenity.name }}</span>
              <b-tag
                :type="proximityType(amenity.proximity)"
                size="is-small"
                rounded
              >
                {{ proximityLabel(amenity.proximity) }}
              </b-tag>
              <span v-if="amenityDistance(amenity)" class="rd-amenity__dist">
                {{ amenityDistance(amenity) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="rd-empty box">
      <b-icon pack="fas" icon="inbox" size="is-large"></b-icon>
      <p>{{ summary.noData }}</p>
    </div>
  </div>
</template>

<script>
import i18n from "@/utils/i18n";
import moment from "moment";

const MONEY_FIELDS = ["price", "cost", "amount"];
const UNIT_KEYS = [
  "UnitType",
  "nbrOfBathrooms",
  "KitchenType",
  "MaxOccupancy",
  "PrivacyOccupancy",
  "AllInclusive",
  "InvTypeInDetail",
];

export default {
  data() {
    return {
      i18n,
      iso: "es",
      raw: {},
    };
  },
  computed: {
    summary() {
      return this.i18n.reserveDetailsSummary[this.iso] || {};
    },
    labels() {
      return (this.i18n.reserveDetailsFields || {})[this.iso] || {};
    },
    hasData() {
      return Object.keys(this.raw).length > 0;
    },
    numberRef() {
      return (
        this.raw.numberRef ||
        this.raw.NumberRef ||
        this.raw.Referencia ||
        this.raw.reference ||
        null
      );
    },
    images() {
      if (Array.isArray(this.raw.imgs) && this.raw.imgs.length)
        return this.raw.imgs;
      if (Array.isArray(this.raw.Images) && this.raw.Images.length)
        return this.raw.Images;
      return [];
    },
    hasCoordinates() {
      return Boolean(this.raw.GPS_Lat && this.raw.GPS_Long);
    },
    allInclusive() {
      return ["y", "yes", "s", "si", "sí", "true", "1"].includes(
        String(this.raw.AllInclusive || "").toLowerCase()
      );
    },
    fullAddress() {
      const parts = [
        this.raw.ResortAddress1,
        this.raw.ResortAddress2,
        this.raw.ResortAddress3,
        this.raw.ResortAddress4,
      ].filter((part) => part && `${part}`.trim() !== "0");

      const tail = [
        this.raw.ResortCity,
        this.raw.ResortState,
        this.raw.ResortCountry,
        this.raw.ResortZip,
      ].filter(Boolean);

      return [...parts, ...tail].join(", ");
    },
    highlights() {
      return this.iso === "en"
        ? this.raw.HighlightsEN
        : this.raw.HighlightsSP;
    },
    importantNotes() {
      const notes =
        this.iso === "en"
          ? this.raw.ImportantNotesEN
          : this.raw.ImportantNotesSP;
      if (!notes) return null;
      return `${notes}`.replace(/<br\s*\/?>\s*$/i, "").trim();
    },
    stats() {
      return [
        {
          key: "checkIn",
          icon: "sign-in-alt",
          label: this.summary.checkIn,
          value: this.raw.CheckInDate
            ? this.formatDate(this.raw.CheckInDate)
            : null,
        },
        {
          key: "checkOut",
          icon: "sign-out-alt",
          label: this.summary.checkOut,
          value: this.raw.CheckOutDate
            ? this.formatDate(this.raw.CheckOutDate)
            : null,
        },
        {
          key: "nights",
          icon: "moon",
          label: this.labels.Nights || this.i18n.nights[this.iso],
          value:
            this.raw.Nights !== undefined && this.raw.Nights !== null
              ? `${this.raw.Nights}`
              : null,
        },
        {
          key: "cost",
          icon: "dollar-sign",
          label: this.labels.Cost || this.i18n.price[this.iso],
          value:
            this.raw.Cost !== undefined && this.raw.Cost !== null
              ? this.formatMoney(this.raw.Cost)
              : null,
        },
      ].filter((stat) => stat.value);
    },
    unitFields() {
      return UNIT_KEYS.filter(
        (key) => this.raw[key] !== undefined && this.raw[key] !== null
      ).map((key) => this.fieldFor(key));
    },
    amenityGroups() {
      if (!Array.isArray(this.raw.Amenities)) return [];
      const groups = [];
      this.raw.Amenities.forEach((entry) => {
        const amenity =
          typeof entry === "string" ? { name: entry } : entry || {};
        const name = amenity.groupType || this.summary.amenities;
        let group = groups.find((item) => item.name === name);
        if (!group) {
          group = { name, items: [] };
          groups.push(group);
        }
        group.items.push(amenity);
      });
      return groups;
    },
  },
  created() {
    this.iso = localStorage.getItem("iso") || "es";
    this.$bus.on("change-iso", () => {
      this.iso = localStorage.getItem("iso") || "es";
    });
    this.buildData();
  },
  methods: {
    closePage() {
      window.close();
    },
    buildData() {
      const stored = localStorage.getItem("reserveDetails");
      if (!stored) return;
      try {
        const obj = JSON.parse(stored);
        if (obj && typeof obj === "object" && !Array.isArray(obj)) {
          this.raw = obj;
        }
      } catch (err) {
        this.raw = {};
      }
    },
    fieldFor(key) {
      const value = this.raw[key];
      const lower = key.toLowerCase();
      const field = {
        key,
        label: this.labels[key] || this.prettify(key),
        type: "text",
        value,
      };

      if (value === null || value === undefined || value === "") {
        field.value = "—";
        return field;
      }

      if (MONEY_FIELDS.includes(lower) && !isNaN(value)) {
        field.type = "money";
        field.value = this.formatMoney(value);
        return field;
      }

      if (typeof value === "object") {
        field.value = JSON.stringify(value);
        return field;
      }

      field.value = `${value}`;
      return field;
    },
    prettify(key) {
      return key
        .replace(/_/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
    formatDate(value) {
      let date = value;
      if (typeof date === "number" && date < 1e12) date *= 1000;
      const parsed = moment(date);
      if (!parsed.isValid()) return `${value}`;
      return parsed.format(this.iso === "en" ? "MM/DD/YYYY" : "DD/MM/YYYY");
    },
    formatMoney(value) {
      const amount = Number(value);
      if (isNaN(amount)) return `${value}`;
      return new Intl.NumberFormat(this.iso === "en" ? "en-US" : "es-CO", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    },
    proximityLabel(proximity) {
      const key = `${proximity || ""}`.toLowerCase();
      if (key === "onsite") return this.summary.proximityOnsite;
      if (key === "nearby") return this.summary.proximityNearby;
      if (key === "offsite") return this.summary.proximityOffsite;
      return proximity;
    },
    proximityType(proximity) {
      const key = `${proximity || ""}`.toLowerCase();
      if (key === "onsite") return "is-success is-light";
      if (key === "nearby") return "is-warning is-light";
      return "is-light";
    },
    amenityDistance(amenity) {
      if (amenity.distanceInMiles) return `${amenity.distanceInMiles} mi`;
      if (amenity.distanceInKms) return `${amenity.distanceInKms} km`;
      return null;
    },
    openGoogleMap() {
      if (!this.hasCoordinates) return;
      window.open(
        `https://www.google.com/maps/@${this.raw.GPS_Lat},${this.raw.GPS_Long},18z?entry=ttu`,
        "_blank"
      );
    },
  },
};
</script>

<style scoped>
.reserve-details {
  padding-bottom: 2rem;
}

.rd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  border-left: 5px solid #0472aa;
}

.rd-header__main {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.rd-header__eyebrow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #8c8c8c;
}

.rd-header__resort {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  color: #0472aa;
  word-break: break-word;
}

.rd-header__subtitle {
  font-size: 14px;
  color: #7a7a7a;
}

.rd-header__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.35rem;
}

.rd-top {
  margin-top: 0.25rem;
}

.rd-carousel {
  padding: 0.5rem;
  margin-bottom: 0;
}

.rd-carousel .carousel {
  max-width: 520px;
  margin: 0 auto;
}

.rd-carousel__item {
  position: relative;
  margin: 0;
  padding-top: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
}

.rd-carousel__item img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rd-carousel__empty {
  position: relative;
  max-width: 520px;
  margin: 0 auto;
  padding-top: 100%;
  border: 1px dashed #dbdbdb;
  border-radius: 0.5rem;
}

.rd-carousel__empty-body {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #b5b5b5;
}

.rd-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.rd-stat {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 0.95rem;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.rd-stat__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #e7f2f9;
  color: #0472aa;
  flex-shrink: 0;
}

.rd-stat__body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.rd-stat__label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #909090;
  font-weight: 600;
}

.rd-stat__value {
  font-size: 15px;
  font-weight: 700;
  color: #31363f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rd-card {
  margin-bottom: 1rem;
}

.rd-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding-bottom: 0.6rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #ededed;
}

.rd-card__head .rd-card__title {
  padding-bottom: 0;
  margin-bottom: 0;
  border-bottom: none;
}

.rd-card__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 16px;
  font-weight: 700;
  color: #31363f;
  padding-bottom: 0.6rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid #ededed;
}

.rd-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0;
}

.rd-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.rd-list__label {
  font-size: 13px;
  color: #8c8c8c;
}

.rd-list__value {
  font-size: 14px;
  font-weight: 600;
  color: #31363f;
  text-align: right;
}

.rd-list__link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #0472aa;
}

.rd-location__address {
  font-size: 15px;
  color: #31363f;
  margin-bottom: 1rem;
}

.rd-location__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.85rem 1.5rem;
}

.rd-location__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  text-align: center;
}

.rd-location__item .rd-list__value {
  text-align: center;
}

.rd-text {
  font-size: 14px;
  color: #4a4a4a;
  line-height: 1.6;
  text-align: justify;
  margin: 0;
}

.rd-text--pre {
  white-space: pre-line;
}

.rd-amenity-group + .rd-amenity-group {
  margin-top: 1rem;
}

.rd-amenity-group__title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #0472aa;
  margin-bottom: 0.6rem;
}

.rd-amenity-group__items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 0.5rem 1rem;
}

.rd-amenity {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 13px;
  color: #31363f;
}

.rd-amenity__name {
  flex-grow: 1;
  min-width: 0;
}

.rd-amenity__dist {
  font-size: 12px;
  color: #909090;
  white-space: nowrap;
}

.rd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: #909090;
  text-align: center;
}

@media screen and (max-width: 768px) {
  .rd-stats {
    grid-template-columns: 1fr;
  }

  .rd-header__resort {
    font-size: 24px;
  }
}
</style>
